import dayjs from 'dayjs'

/**
 * Check if an order's expiry date is before today (local calendar day).
 * A missing or invalid expiry date never counts as expired.
 * @param {Object} order - The order object.
 * @returns {boolean} - Returns true if the order has expired, otherwise false.
 */
export const isOrderExpired = (order) => {
    if (!order?.expiryDate) return false
    const expiryDate = dayjs(order.expiryDate)
    return expiryDate.isValid() && expiryDate.isBefore(dayjs(), 'day')
}

/**
 * Select order item by id and return warnings.
 * @param {Object} orderItem - The order item object.
 * @returns {Array|boolean} - Returns an array of warnings if they exist, otherwise false.
 */
export const orderItemWarnings = (orderItem) => {
    return orderItem?.issues?.warnings?.length ? orderItem.issues.warnings : false
}

/**
 * Select order item by id and return errors.
 * @param {Object} orderItem - The order item object.
 * @returns {Array|boolean} - Returns an array of errors if they exist, otherwise false.
 */
export const orderItemErrors = (orderItem) => {
    return orderItem?.issues?.errors?.length ? orderItem.issues.errors : false
}

/**
 * Select order item by id and return if it is unset.
 * @param {Object} orderItem - The order item object.
 * @returns {boolean} - Returns true if the order item is unset, otherwise false.
 */
export const orderItemUnset = (orderItem) => {
    // Check basic required fields
    const basicFieldsUnset = !orderItem?.cuttingTechnologyId || !orderItem?.materialId || !orderItem?.thickness

    // Check if secondary processes with custom properties have values
    let secondaryProcessUnset = false
    if (orderItem?.secondaryProcesses?.length) {
        secondaryProcessUnset = orderItem.secondaryProcesses.some(
            (process) => process.customPropertyName && !process.customValue
        )
    }

    // Check if manual folding with numberOfFolds > 0 has a maxFoldLength value
    let foldingUnset = false
    if (orderItem?.foldingSource === 'Manual' && orderItem?.numberOfFolds > 0) {
        foldingUnset = !orderItem.maxFoldLength
    }

    return basicFieldsUnset || secondaryProcessUnset || foldingUnset
}

/**
 * Select order item by id and return if it is successful.
 * @param {Object} orderItem - The order item object.
 * @returns {boolean} - Returns true if the order item has no warnings or errors, otherwise false.
 */
export const orderItemSuccess = (orderItem) => {
    return !orderItem?.issues?.warnings?.length && !orderItem?.issues?.errors?.length
}

/**
 * Select order item by id and return if it is calculated.
 * @param {Object} orderItem - The order item object.
 * @returns {boolean} - Returns true if the order item has itemPrice and linePrice, otherwise false.
 */
export const orderItemCalculated = (orderItem) => {
    return Boolean(orderItem?.itemPrice) && Boolean(orderItem?.linePrice)
}

/**
 * Check if any order items have issues (warnings or errors).
 * @param {Array} orderItems - The array of order items.
 * @returns {boolean} - Returns true if any order item has issues, otherwise false.
 */
export const orderHasIssues = (orderItems) => {
    if (!orderItems?.length) return false
    return orderItems.some((item) =>
        Object.keys(item?.issues).some((key) => {
            if (key !== '$type') {
                return item?.issues[key]?.length
            }
        })
    )
}

/**
 * Check if any order items are unset.
 * @param {Array} orderItems - The array of order items.
 * @returns {boolean} - Returns true if any order item is unset, otherwise false.
 */
export const orderHasUnsetItems = (orderItems) => {
    if (!orderItems?.length) return false
    return orderItems.some((item) => orderItemUnset(item))
}

/**
 * Count how many order items have warnings, errors, or are unset.
 * @param {Array} orderItems - The array of order items.
 * @returns {Object} - Returns an object with counts of unset, warnings, errors, and success.
 */
export const orderIssuesCount = (orderItems) => {
    if (!orderItems?.length) return false
    return orderItems.reduce(
        (acc, item) => {
            if (orderItemUnset(item)) acc.unset += 1
            if (!orderItemUnset(item) && orderItemWarnings(item)) acc.warnings += 1
            if (!orderItemUnset(item) && orderItemErrors(item)) acc.errors += 1
            if (!orderItemUnset(item) && orderItemSuccess(item)) acc.success += 1
            return acc
        },
        { unset: 0, warnings: 0, errors: 0, success: 0 }
    )
}

/**
 * Select order item by id and check if it has warnings or errors.
 * @param {Object} orderItem - The order item object.
 * @returns {boolean} - Returns true if the order item has issues, otherwise false.
 */
export const orderItemHasIssues = (orderItem) => {
    if (!orderItem?.issues) return false
    return Object.keys(orderItem?.issues).some((key) => {
        if (key !== '$type') {
            return orderItem?.issues[key]?.length
        }
    })
}

/**
 * Sort by user-defined index.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByUserDefinedIndex = (isAsc) => (a, b) => {
    return isAsc ? a.index - b.index : b.index - a.index
}

/**
 * Sort by default (creation date).
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByDefault = (isAsc) => (a, b) => {
    const dateA = new Date(a.createDateUtc)
    const dateB = new Date(b.createDateUtc)
    return isAsc ? dateA - dateB : dateB - dateA
}

/**
 * Sort by name.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByName = (isAsc) => (a, b) => {
    return isAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
}

/**
 * Sort by quantity.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByQuantity = (isAsc) => (a, b) => {
    return isAsc ? a.quantity - b.quantity : b.quantity - a.quantity
}

/**
 * Sort by unit price.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByUnitPrice = (isAsc) => (a, b) => {
    return isAsc ? a.itemPrice - b.itemPrice : b.itemPrice - a.itemPrice
}

/**
 * Sort by material thickness.
 * @param {boolean} isAsc - Sort in ascending order if true, otherwise descending.
 * @param {Object} materials - The materials object.
 * @returns {Function} - Returns a comparison function for sorting.
 */
const sortByMaterialThickness = (isAsc, materials) => (a, b) => {
    const materialArray = Object.values(materials)

    const materialA = materialArray.find((material) => material.materialId === a.materialId)
    const materialB = materialArray.find((material) => material.materialId === b.materialId)

    const materialThicknessA = {
        materialName: materialA?.materialName ?? '',
        thickness: a.thickness,
    }
    const materialThicknessB = {
        materialName: materialB?.materialName ?? '',
        thickness: b.thickness,
    }

    if (materialThicknessA.materialName === materialThicknessB.materialName) {
        return isAsc
            ? materialThicknessA.thickness - materialThicknessB.thickness
            : materialThicknessB.thickness - materialThicknessA.thickness
    }

    return isAsc
        ? materialThicknessA.materialName.localeCompare(materialThicknessB.materialName)
        : materialThicknessB.materialName.localeCompare(materialThicknessA.materialName)
}

/**
 * Get the sorting function based on selected criteria and direction.
 * @param {string} selectedCriteria - The selected criteria for sorting.
 * @param {string} selectedDirection - The selected direction for sorting ('ASC' or 'DESC').
 * @param {Object} materials - The materials object.
 * @returns {Function} - Returns the appropriate sorting function.
 */
export const getSortFunction = (selectedCriteria, selectedDirection, materials) => {
    const isAsc = selectedDirection === 'ASC'
    switch (selectedCriteria) {
        case 'Default':
            return sortByDefault(isAsc)
        case 'Name':
            return sortByName(isAsc)
        case 'MaterialThickness':
            return sortByMaterialThickness(isAsc, materials)
        case 'Quantity':
            return sortByQuantity(isAsc)
        case 'UnitPrice':
            return sortByUnitPrice(isAsc)
        default:
            return sortByUserDefinedIndex(isAsc)
    }
}

/**
 * Split an order's "Secondary processes" amount (folding plus secondary processes) into display rows.
 * Processes the customer can't see, or that are missing from the organisation's list, are grouped into
 * one 'Other' row. Rows are rounded to the currency decimals so they add up to the rounded total.
 * @param {Object} params
 * @param {number} [params.fractionDigits=2] - Decimals of the order currency.
 * @param {Object} params.order - The order, for its foldingTotalPrice.
 * @param {Array} params.orderItems - The order items, each with quantity and secondaryProcesses[].
 * @param {Array} params.secondaryProcesses - The organisation's process list, sorted by index.
 * @returns {Array<{id: string, name?: string, total: number}>} - The 'Folding' row, one row per visible
 * process in index order, then the 'Other' row. Rows with a zero total are left out.
 */
export const getSecondaryProcessRows = ({ fractionDigits = 2, order, orderItems, secondaryProcesses }) => {
    const sum = (values) => values.reduce((total, value) => total + value, 0)

    const totals = new Map()
    for (const { quantity, secondaryProcesses: itemProcesses } of orderItems ?? []) {
        for (const { pricePerItem, secondaryProcessId, setupPricePerItem } of itemProcesses ?? []) {
            const total = ((pricePerItem || 0) + (setupPricePerItem || 0)) * (quantity || 0)
            totals.set(secondaryProcessId, (totals.get(secondaryProcessId) ?? 0) + total)
        }
    }

    const processRows = (secondaryProcesses ?? [])
        .filter(({ customerVisible, secondaryProcessId }) => customerVisible && totals.has(secondaryProcessId))
        .map(({ name, secondaryProcessId }) => ({
            id: secondaryProcessId,
            name,
            total: totals.get(secondaryProcessId),
        }))
    const shownIds = new Set(processRows.map(({ id }) => id))
    const otherTotals = [...totals].filter(([id]) => !shownIds.has(id)).map(([, total]) => total)
    const rows = [
        { id: 'Folding', total: order?.foldingTotalPrice || 0 },
        ...processRows,
        { id: 'Other', total: sum(otherTotals) },
    ]

    // Largest remainder: round every row down, then give the missing units to the rows that lost the most
    const factor = 10 ** fractionDigits
    const exact = rows.map(({ total }) => total * factor)
    const units = exact.map(Math.floor)
    const missingUnits = Math.round(sum(exact)) - sum(units)
    exact
        .map((_, index) => index)
        .sort((a, b) => exact[b] - units[b] - (exact[a] - units[a]))
        .slice(0, missingUnits)
        .forEach((index) => (units[index] += 1))

    return rows.map((row, index) => ({ ...row, total: units[index] / factor })).filter(({ total }) => total > 0)
}
